import { Box, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Accordion } from '@europrocurement/flexy-components';
import { useFormaliteService, type DossierOpenSearch } from '@europrocurement/l2d-domain';
import { type ApiDossiersIdNotesGet200ResponseNotesInner } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { IaButton } from '@europrocurement/l2d-modules/modules/IaServices';
import { useSnackbar } from 'notistack';
import GenericModalMailContent from '@europrocurement/l2d-modules/modules/MailForm/components/GenericModalMailContent/GenericModalMailContent';
import { UseFormReturn } from 'react-hook-form';
import { FormObject } from '@europrocurement/flexy-form';
import { markDownToPlainText } from '@europrocurement/l2d-utils';
import { NotesSelectorForm } from '@europrocurement/l2d-modules/modules/Notes';
import { faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons';
import { type DisplayedDocument } from '../../../providers/types';
import DocumentsSelectorsForm from '../DocumentsSelectorsForm/DocumentsSelectorsForm';
import { Tiers } from '../../../hooks/useDossierPrescripteur';

type dossierWithNotes = {
    dossier: DossierOpenSearch;
    notes: ApiDossiersIdNotesGet200ResponseNotesInner[];
};

type AdditionnalCollectionRequestFormProps = {
    missingDocs: DisplayedDocument[];
    dossiers: DossierOpenSearch[];
    dossier: DossierOpenSearch;
    prescriber: Tiers;
};

const AdditionnalCollectionRequestForm: React.FunctionComponent<AdditionnalCollectionRequestFormProps> =
    function ({ missingDocs, dossiers, dossier, prescriber }) {
        const [documentsIds, setDocumentsIds] = useState<number[]>([]);
        const [selectedDocuments, setSelectedDocuments] = useState<DisplayedDocument[]>([]);
        const [selectedNotes, setSelectedNotes] = useState<
            ApiDossiersIdNotesGet200ResponseNotesInner[]
        >([]);
        const [preSelectedNotesIds, setPreSelectedNotesIds] = useState<number[]>([]);
        const [dossiersWithNotes, setDossiersWithNotes] = useState<dossierWithNotes[]>([]);
        const [childFormContext, setChildFormContext] = useState<UseFormReturn<FormObject>>(); // GetFormContext from child

        // Blocs text
        const [textContentDocs, setTextContentDocs] = useState<string>('');
        const [textContentNotes, setTextContentNotes] = useState<string>('');
        const [textContent, setTextContent] = useState('');

        const { dossierApi } = useFormaliteService();
        const { enqueueSnackbar } = useSnackbar();

        useEffect(() => {
            if (missingDocs) {
                setDocumentsIds(missingDocs.map((doc) => doc.documentTypeId as number));
            }
        }, [missingDocs]);

        const loadDossiersNotes = async () => {
            const results = await Promise.all(
                dossiers.map(async (doss) => {
                    if (!doss.id) return null;

                    const { data } = await dossierApi.apiDossiersIdNotesGet({
                        id: doss.id,
                        memoClient: true,
                    });

                    if (!data?.notes?.length) return null;

                    return {
                        dossier: doss,
                        notes: data.notes as ApiDossiersIdNotesGet200ResponseNotesInner[],
                    };
                }),
            );

            // Remove null data if existing
            const dossierWithNotesResponse = results.filter(
                (res) => res && res.notes && res.notes.length > 0,
            ) as dossierWithNotes[];

            // Update State dossier with Notes
            setDossiersWithNotes(dossierWithNotesResponse);

            // Extract notes ids
            const notesIds: number[] = [];
            dossierWithNotesResponse.forEach((dossNotes) =>
                dossNotes.notes.forEach((note) => notesIds.push(note.id as number)),
            );
            setPreSelectedNotesIds(notesIds);
        };
        useEffect(() => {
            loadDossiersNotes();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [dossiers]);

        const getFormContext = (fromCtxt: UseFormReturn<FormObject>) => {
            setChildFormContext(fromCtxt);
        };

        // Mulitple use effect to update differents parts of mail and textArea
        //
        // Update text Content doc
        useEffect(() => {
            let textContentDoc = '';
            if (selectedDocuments.length > 0) {
                textContentDoc += `\n\nDocuments manquants :`;
                selectedDocuments.forEach((doc) => {
                    textContentDoc += `\n- ${doc.label}`;
                });
            }
            setTextContentDocs(textContentDoc);
        }, [selectedDocuments]);

        // Update text Content notes
        useEffect(() => {
            let textContentnote = '';
            if (selectedNotes.length > 0) {
                textContentnote += `\n\nInformations manquantes :`;

                selectedNotes.forEach((note) => {
                    textContentnote += `\n ${note.content}`;
                });
            }
            setTextContentNotes(textContentnote);
        }, [selectedNotes]);

        // Update composed textContent
        useEffect(() => {
            let txtContent = `Bonjour,\n\nAfin de compléter votre dossier, nous vous invitons à nous communiquer les éléments suivants :`;
            txtContent += textContentNotes;
            txtContent += textContentDocs;
            txtContent += `\n\nMerci d'avance pour votre retour.\n\nBien cordialement,\nL'équipe Formalités L2D`;
            setTextContent(txtContent);
        }, [textContentDocs, textContentNotes]);

        // Update form context textArea
        useEffect(() => {
            if (childFormContext) {
                childFormContext.setValue('mailBody', markDownToPlainText(textContent));
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [textContent]);

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        width: '50%',
                        padding: '16px',
                        maxHeight: '75vh',
                        overflow: 'auto',
                    }}
                >
                    <Box>
                        <Typography variant="l2dh6">Demander des informations</Typography>
                        {dossier.collectionNumero && (
                            <Typography
                                variant="l2dcaption"
                                sx={{ color: 'text.gris70' }}
                            >
                                Commande #{dossier.collectionNumero}
                            </Typography>
                        )}
                    </Box>

                    {dossiersWithNotes && dossiersWithNotes.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <Typography variant="l2dsubtitles">Notes</Typography>

                            {dossiersWithNotes.map((dossWithNotes) => (
                                <Accordion
                                    key={`Notes_${dossWithNotes.dossier.numero}`}
                                    title={<Typography>{dossWithNotes.dossier.numero} </Typography>}
                                    expanded
                                >
                                    <NotesSelectorForm
                                        notes={dossWithNotes.notes}
                                        setSelectedNotes={setSelectedNotes}
                                        preSelectedNotesIds={preSelectedNotesIds}
                                    />
                                </Accordion>
                            ))}
                        </Box>
                    )}

                    {missingDocs.length > 0 && documentsIds && (
                        <Box>
                            <Typography variant="l2dsubtitles">Documents manquants</Typography>

                            <DocumentsSelectorsForm
                                documents={missingDocs}
                                preSelectedDocumentsIds={documentsIds}
                                setSelectedDocuments={setSelectedDocuments}
                            />
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        width: '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        padding: '16px',
                        maxHeight: '75vh',
                        overflow: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="l2dh6">Message généré</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <IaButton
                                getParams={() => ({
                                    textContent: childFormContext?.getValues('mailBody'),
                                })}
                                onSuccess={(data) => {
                                    const correctedText = data.answer;
                                    if (correctedText) {
                                        setTextContent(correctedText);
                                        enqueueSnackbar('Correction effectuée');
                                    }
                                }}
                            />
                            <IaButton
                                applicationName="L2D_Resume"
                                label="Améliorer le texte"
                                icon={faWandMagicSparkles}
                                getParams={() => ({
                                    textContent: childFormContext?.getValues('mailBody'),
                                })}
                                onSuccess={(data) => {
                                    const correctedText = data.answer;
                                    if (correctedText) {
                                        setTextContent(correctedText);
                                        enqueueSnackbar('Amélioration effectuée');
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                    {textContent ? (
                        <GenericModalMailContent
                            to={[]}
                            showTo
                            subject="Demande de renseignement"
                            showSubject
                            body={textContent}
                            prescriber={prescriber}
                            showBody
                            attachments={[]}
                            showAttachments
                            getFormContext={getFormContext}
                            autoBalisesForMail
                        />
                    ) : (
                        <Skeleton sx={{ minHeight: '700px', marginTop: '-100px' }} />
                    )}
                </Box>
            </Box>
        );
    };

export default AdditionnalCollectionRequestForm;
